import { callGeneratePdf } from './callGeneratePdf';

/** Defines the parameters required for the pdfExport function. */
export interface PdfExportParams {
    /** React ref pointing to the HTMLDivElement containing the content to be exported. */
    contentRef: React.RefObject<HTMLDivElement | null>;
    /** Specifies the desired output format of the export operation. Defaults to 'save'. */
    outputType?: 'save' | 'blob' | 'datauristring';
    /** Optional base64 Data URI string for the logo image to be placed in the header. */
    headerLogoDataUrl?: string;
    /** Optional logo size (CSS value like "20px") for the PDF header. */
    headerLogoSize?: string;
    /** Optional text to be placed in the header, aligned to the right. */
    headerRightText?: string;
    /** Optional array of strings or single string for the footer, aligned to the left. Each string in the array will be a new line. */
    footerLeftText?: string | string[];
    /** Filename for the saved PDF, including .pdf extension. */
    filename?: string;
}

/**
 * Exports the content of a given React component ref to a PDF document.
 * Uses server-side Puppeteer to create true vector PDFs with selectable text.
 * 
 * @param params - The parameters for the PDF export operation.
 * @returns A Promise that resolves with the PDF content (Blob or string based on outputType) or void if saving directly.
 */
export const pdfExport = async ({
    contentRef,
    outputType = 'save',
    headerLogoDataUrl,
    headerLogoSize,
    headerRightText,
    footerLeftText,
    filename,
}: PdfExportParams): Promise<Blob | string | void> => {
    if (!contentRef.current) {
        console.warn('Content reference is null or undefined.');
        throw new Error('Content reference is null or undefined');
    }

    try {
        switch (outputType) {
            case 'blob':
                return await callGeneratePdf({
                    contentRef,
                    headerLogoDataUrl,
                    headerLogoSize,
                    headerRightText,
                    footerLeftText
                });
            
            case 'datauristring': {
                const blob = await callGeneratePdf({
                    contentRef,
                    headerLogoDataUrl,
                    headerLogoSize,
                    headerRightText,
                    footerLeftText
                });
                return new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            }
            
            case 'save':
            default: {
                const blob = await callGeneratePdf({
                    contentRef,
                    headerLogoDataUrl,
                    headerLogoSize,
                    headerRightText,
                    footerLeftText
                });

                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename ?? 'document.pdf';
                document.body.appendChild(link);
                link.click();

                const cleanup = () => {
                    if (link.parentNode) {
                        link.parentNode.removeChild(link);
                    }
                    URL.revokeObjectURL(url);
                };

                if (typeof window.requestAnimationFrame === 'function') {
                    window.requestAnimationFrame(cleanup);
                } else {
                    setTimeout(cleanup, 0);
                }
                return;
            }
        }
    } catch (error) {
        console.error('Error during PDF export:', error);
        throw error;
    }
};